import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { toast, ToastContainer } from 'react-toastify';
import { ArrowUp, X, Download, Info, User, FileText, CheckCircle, XCircle, Map, Database, GitMerge, FileBadge, Tag, Palette, Users, ShieldCheck, Globe, AlertTriangle, Send, FileCheck, FileX, DownloadCloud, ClipboardCheck, Edit3 } from "lucide-react";
import config from '../../config/config';
import DashboardLayout from '../shared/layout/DashboardLayout';
import { getSupervisorMenuItems } from '../../utils/menuItems';
import { useUnsealModal } from '../../hooks/useUnsealModal';
import UnsealProgressModal from '../../components/UnsealProgressModal';
import DataChecklistView from '../../components/DataChecklistView';
import MetadataEditor from '../../components/MetadataEditor/MetadataEditor';
import '../../styles/RekapDataDetails.css';
import 'react-toastify/dist/ReactToastify.css';

// Komponen Modal Konfirmasi
const ConfirmationModal = ({ isOpen, onClose, onConfirm, loading, ticketStatus, siapPublikasi, onSiapPublikasiChange, jenisIgt }) => {
  if (!isOpen) return null;
  const isRenaksiPKSP = jenisIgt === 'Renaksi PKSP';

  const getPopupContent = () => {
    if (!ticketStatus) {
      return {
        title: 'Status Belum Dipilih',
        message: 'Silakan pilih status "Close Tiket" terlebih dahulu sebelum mengirim.',
        confirmButtonClass: 'btn-secondary',
        confirmText: 'Mengerti',
        icon: <AlertTriangle size={24} />,
        disableConfirm: true,
        showPublikasiOption: false,
      };
    }
    if (ticketStatus === 'OK') {
      return {
        title: 'Konfirmasi Persetujuan Tiket',
        message: 'Anda akan menyetujui tiket ini dan mengirim Rekomendasi Integrasi. Pastikan dokumen yang diunggah sudah benar.',
        confirmButtonClass: 'btn-confirm',
        confirmText: 'Ya, Setujui & Kirim',
        icon: <FileCheck size={24} />,
        disableConfirm: false,
        showPublikasiOption: false,
      };
    }
    return {
      title: 'Konfirmasi Penolakan Tiket',
      message: isRenaksiPKSP 
        ? 'Anda akan menolak tiket ini dan mengirim Rekomendasi Hasil Verifikasi. Apakah data ini siap untuk dipublikasi meskipun ditolak?'
        : 'Anda akan menolak tiket ini dan mengirim Rekomendasi Hasil Verifikasi.',
      confirmButtonClass: 'btn-danger',
      confirmText: 'Ya, Tolak & Kirim',
      icon: <FileX size={24} />,
      disableConfirm: isRenaksiPKSP && siapPublikasi === null, // Hanya disable jika Renaksi PKSP dan belum pilih
      showPublikasiOption: isRenaksiPKSP, // Hanya tampilkan untuk Renaksi PKSP
    };
  };

  const content = getPopupContent();

  return (
    <div className="survey-modal-overlay">
      <div className="survey-modal" style={{ maxWidth: '520px' }}>
        <div className="survey-header">
          <div className="survey-header-content">
            {content.icon}
            <div>
              <h3>{content.title}</h3>
              <p>{content.message}</p>
            </div>
          </div>
        </div>
        {content.showPublikasiOption && (
          <div style={{ padding: '0 30px 20px 30px' }}>
            <label style={{ display: 'block', marginBottom: '12px', fontWeight: '600', fontSize: '14px' }}>
              Apakah data siap untuk dipublikasi?
            </label>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="button"
                onClick={() => onSiapPublikasiChange(true)}
                className={`btn ${siapPublikasi === true ? 'btn-success' : 'btn-outline-success'}`}
                style={{ flex: 1 }}
              >
                <CheckCircle size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                Ya, Siap Publikasi
              </button>
              <button
                type="button"
                onClick={() => onSiapPublikasiChange(false)}
                className={`btn ${siapPublikasi === false ? 'btn-warning' : 'btn-outline-warning'}`}
                style={{ flex: 1 }}
              >
                <XCircle size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                Tidak Siap Publikasi
              </button>
            </div>
            {siapPublikasi === null && (
              <small className="text-danger" style={{ display: 'block', marginTop: '8px' }}>
                Silakan pilih apakah data siap untuk dipublikasi atau tidak.
              </small>
            )}
          </div>
        )}
        <div className="form-actions" style={{ padding: '20px 30px', borderTop: 'none' }}>
          <button onClick={onClose} className="btn-cancel" disabled={loading}>
            Batal
          </button>
          <button onClick={onConfirm} className={content.confirmButtonClass} disabled={loading || content.disableConfirm}>
            {loading ? 'Memproses...' : content.confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};


const DataSVDetailPage = () => {
  const { no_tiket } = useParams();
  const navigate = useNavigate();
  const suratRekomendasiRef = useRef(null);
  const baRef = useRef(null);

  const [dataDetail, setDataDetail] = useState(null);
  const [profil, setProfil] = useState(null);
  const [catatan, setCatatan] = useState({});
  const [verificationDocs, setVerificationDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [statusError, setStatusError] = useState('');
  const [hasDownloaded, setHasDownloaded] = useState({
    ba: false,
    rekom: false
  });
  const [downloadingTemplate, setDownloadingTemplate] = useState({
    ba: false,
    rekom: false
  });

  const [statusChoices, setStatusChoices] = useState({ tiket: '' });
  const [isChecklistViewOpen, setIsChecklistViewOpen] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState('');
  const [checklistStatus, setChecklistStatus] = useState({});
  const [availableChecklists, setAvailableChecklists] = useState({});
  const [siapPublikasi, setSiapPublikasi] = useState(null); // null = belum dipilih, true = siap, false = tidak siap
  
  // Metadata editor states
  const [showMetadataEditor, setShowMetadataEditor] = useState(false);
  const [metadataContent, setMetadataContent] = useState(null);
  const [metadataUrl, setMetadataUrl] = useState('');
  
  // Capaian states (read-only)
  const [capaianData, setCapaianData] = useState([]);
  const [kepkaInfo, setKepkaInfo] = useState(null);


  const handleChecklistViewOpen = (documentType) => {
    setSelectedDocumentType(documentType);
    setIsChecklistViewOpen(true);
  };

  const handleChecklistViewClose = () => {
    setIsChecklistViewOpen(false);
    setSelectedDocumentType('');
  };

  // Handle opening Metadata Editor
  const handleOpenMetadataEditor = async () => {
    if (!dataDetail || !dataDetail.metadata) {
      toast.warning('File metadata tidak tersedia');
      return;
    }
    const metadataUrl = dataDetail.metadata;
    if (!metadataUrl.toLowerCase().endsWith('.xml')) {
      toast.warning('Hanya file XML yang bisa dilihat di editor');
      return;
    }
    
    try {
      setMetadataUrl(metadataUrl);
      const token = localStorage.getItem('token');
      
      // Download via metadata API endpoint using noTiket parameter
      const response = await axios.get(
        `${config.API_URL}/metadata/download`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          params: { noTiket: dataDetail.no_tiket },
          responseType: 'text'
        }
      );
      
      setMetadataContent(response.data);
      setShowMetadataEditor(true);
    } catch (error) {
      console.error('Error loading metadata:', error);
      toast.error('Gagal memuat file metadata');
    }
  };

  const handleCloseMetadataEditor = () => {
    setShowMetadataEditor(false);
    setMetadataContent(null);
  };

  // Fungsi untuk mengecek apakah ada data checklist
  const checkChecklistData = async (noTiket) => {
    if (!noTiket) return;

    const allDocumentTypes = ['peta', 'struktur_data', 'takel', 'dokumen', 'metadata', 'simbology', 'role', 'kualitas', 'cakupan'];
    const statusMap = {};
    const availabilityMap = {};

    // Initialize all document types
    allDocumentTypes.forEach(docType => {
      statusMap[docType] = false;
      availabilityMap[docType] = false;
    });

    const token = localStorage.getItem('token');

    // First, check which document types have checklist items
    try {
      const availabilityPromises = allDocumentTypes.map(async (docType) => {
        try {
          const response = await axios.get(
            `${config.API_URL}/api/checklist/${docType}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          return { [docType]: response.data.checklist.length > 0 };
        } catch (error) {
          return { [docType]: false };
        }
      });

      const availabilityResults = await Promise.all(availabilityPromises);
      availabilityResults.forEach(result => Object.assign(availabilityMap, result));
      setAvailableChecklists(availabilityMap);
    } catch (error) {
      console.error('Error checking checklist availability:', error);
    }

    // Then check which documents have checklist data filled
    try {
      const promises = allDocumentTypes.map(async (docType) => {
        // Skip if no checklist available for this document type
        if (!availabilityMap[docType]) {
          return { [docType]: false };
        }
        try {
          const response = await fetch(`${config.API_URL}/api/checklist/data/${noTiket}/${docType}`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          if (response.status === 404) {
            return { [docType]: false };
          }

          if (response.ok) {
            const data = await response.json();
            if (data && data.checklistData) {
              const items = data.checklistData.items || {};
              return { [docType]: Object.keys(items).length > 0 };
            }
          }

          return { [docType]: false };
        } catch (error) {
          // Silent handling
          return { [docType]: false };
        }
      });

      const results = await Promise.all(promises);
      results.forEach(result => Object.assign(statusMap, result));
      setChecklistStatus(statusMap);
    } catch (error) {
      console.error('Error checking checklist data:', error);
      setChecklistStatus(statusMap);
    }
  };

  const documentItems = [
    { label: 'Peta', key: 'peta', icon: <Map className="document-lucide-icon" /> },
    { label: 'Struktur Data', key: 'struktur_data', icon: <Database className="document-lucide-icon" /> },
    { label: 'Mekanisme dan Tata Kerja', key: 'takel', icon: <GitMerge className="document-lucide-icon" /> },
    { label: 'Dokumen Penetapan', key: 'dokumen', icon: <FileBadge className="document-lucide-icon" /> },
    { label: 'Metadata', key: 'metadata', icon: <Tag className="document-lucide-icon" /> },
    { label: 'Simbology', key: 'simbology', icon: <Palette className="document-lucide-icon" /> },
    { label: 'Role Akses', key: 'role', icon: <Users className="document-lucide-icon" /> },
    { label: 'Kontrol Kualitas', key: 'kualitas', icon: <ShieldCheck className="document-lucide-icon" /> },
  ];
  const {
    isUnsealModalOpen,
    selectedTicket,
    openUnsealModal,
    closeUnsealModal,
    handleUnsealComplete
  } = useUnsealModal();

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleStatusChange = (e, field) => setStatusChoices({ ...statusChoices, [field]: e.target.value });
  const validateFileType = (file) => file && file.type === 'application/pdf';

  const sanitizeFileName = (fileName) => {
    return fileName
      .replace(/\s+/g, '_')
      .replace(/[^a-zA-Z0-9_.-]/g, '');
  };

  const downloadTemplate = async (templateType) => {
    const endpoint = templateType === 'ba' ? 'template-ba-filled' : 'template-rekom-filled';
    const fileName = templateType === 'ba' ? `Rekomendasi_Integrasi_${atob(no_tiket)}.pdf` : `Rekomendasi_Hasil_Verifikasi_${atob(no_tiket)}.pdf`;
    setDownloadingTemplate(prev => ({ ...prev, [templateType]: true }));

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${config.API_URL}/template/${endpoint}?no_tiket=${atob(no_tiket)}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
        onDownloadProgress: (progressEvent) => {
          if (progressEvent.lengthComputable) {
            // Progress tracking available if needed
          }
        }
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Dokumen Template berhasil diunduh.`);
      setHasDownloaded(prev => ({ ...prev, [templateType]: true }));
    } catch (error) {
      toast.error(`Gagal mengunduh dokumen PDF.`);
    } finally {
      // Clear loading state
      setDownloadingTemplate(prev => ({ ...prev, [templateType]: false }));
    }
  };

  const uploadFiles = async (token) => {
    const formData = new FormData();
    let hasFiles = false;

    if (suratRekomendasiRef.current?.files[0]) {
      const rekomFile = suratRekomendasiRef.current.files[0];
      if (!validateFileType(rekomFile)) { toast.error("Format Surat Rekomendasi harus PDF."); return false; }
      formData.append('suratRekomendasi', new File([rekomFile], sanitizeFileName(rekomFile.name), { type: rekomFile.type }));
      hasFiles = true;
    }

    if (baRef.current?.files[0]) {
      const baFile = baRef.current.files[0];
      if (!validateFileType(baFile)) { toast.error("Format Berita Acara harus PDF."); return false; }
      formData.append('ba', new File([baFile], sanitizeFileName(baFile.name), { type: baFile.type }));
      hasFiles = true;
    }

    if (hasFiles) {
      await axios.post(`${config.API_URL}/dataAdmin/upload/${atob(no_tiket)}`, formData, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
    }
    return true;
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();

    // Validasi status tiket
    if (!statusChoices.tiket) {
      toast.error("Harap pilih status 'Close Tiket' terlebih dahulu!");
      setStatusError("Pilihan status wajib diisi.");
      return;
    }

    // Validasi status tiket, harus 'Sedang Verifikasi'
    if (dataDetail.status !== 'Sedang Verifikasi') {
      toast.error("Verifikator belum menyelesaikan proses verifikasi. Form tidak dapat dikirim.");
      return;
    }

    // Validasi upload dokumen
    if (statusChoices.tiket === 'OK' && !dataDetail.batte && !baRef.current?.files[0]) {
      toast.error("Untuk menyetujui, Anda wajib mengunggah 'Rekomendasi Integrasi'.");
      return;
    }
    if (statusChoices.tiket === 'Tidak' && !dataDetail.rekom && !suratRekomendasiRef.current?.files[0]) {
      toast.error("Untuk menolak, Anda wajib mengunggah 'Rekomendasi Hasil Verifikasi'.");
      return;
    }

    setStatusError('');
    setIsConfirmModalOpen(true);
  };

  const handleConfirmSubmit = async () => {
    setIsSaving(true);
    try {
      const token = localStorage.getItem('token');
      const decodedToken = jwtDecode(token);

      const filesValidAndUploaded = await uploadFiles(token);
      if (filesValidAndUploaded === false) {
        setIsSaving(false);
        setIsConfirmModalOpen(false);
        return;
      }

      const notesToSubmit = {};
      const statusChoicesToSubmit = { tiket: statusChoices.tiket };

      Object.keys(catatan).forEach(key => {
        if (key === 'tiket') return;
        
        const parts = String(catatan[key] || '').split('|');
        notesToSubmit[key] = parts[0] || '';
        statusChoicesToSubmit[key] = parts[1] || 'Tidak Sesuai';
      });

      const submissionData = {
        notes: notesToSubmit,
        statusChoices: statusChoicesToSubmit,
        username: decodedToken.username,
        role: decodedToken.role,
        verificationDocs: verificationDocs,
        siapPublikasi: statusChoices.tiket === 'Tidak' ? siapPublikasi : null, // Kirim info siap publikasi jika tolak
      };

      await axios.post(`${config.API_URL}/dataAdmin/submits/${atob(no_tiket)}`, submissionData, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      toast.success('Status tiket berhasil dikirim!');
      const mode = statusChoices.tiket === 'OK' ? 'a' : 'c';
      navigate(`/admin/success?no_tiket=${atob(no_tiket)}&mode=${mode}`);

    } catch (err) {
      toast.error('Gagal mengirim status tiket. Silakan coba lagi.');
    } finally {
      setIsSaving(false);
      setIsConfirmModalOpen(false);
    }
  };

  const downloadVerificationDoc = async (docIndex) => {
    try {
      const token = localStorage.getItem('token');
      const url = `${config.API_URL}/dataAdmin/downloadVerificationDoc/${btoa(dataDetail.no_tiket)}/${docIndex}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }, responseType: 'blob',
      });
      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `Verifikasi_${dataDetail.no_tiket}_Catatan${docIndex}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      toast.error('Gagal mengunduh dokumen verifikasi.');
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        if (!token) { navigate('/'); return; }
        const decodedToken = jwtDecode(token);
        if (!decodedToken.twoFactorVerified || decodedToken.role !== 2) {
          navigate('/');
          return;
        }

        const detailResponse = await axios.get(`${config.API_URL}/dataAdmin/details/${atob(no_tiket)}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });

        const detailData = detailResponse.data;
        setDataDetail(detailData);
        setProfil(detailData.user);
        checkChecklistData(detailData.no_tiket);
        
        // Load capaian data
        if (detailData.capaian_data) {
          try {
            const parsedCapaian = typeof detailData.capaian_data === 'string' 
              ? JSON.parse(detailData.capaian_data) 
              : detailData.capaian_data;
            setCapaianData(parsedCapaian);
          } catch (error) {
            console.error('Error parsing capaian_data:', error);
            setCapaianData([]);
          }
        }
        
        // Load kepka info if available
        if (detailData.kepka_id) {
          try {
            const kepkaResponse = await axios.get(`${config.API_URL}/api/management/kepka/${detailData.kepka_id}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            // Backend sends { success: true, data: {...} }
            const kepkaData = kepkaResponse.data.data || kepkaResponse.data;
            if (kepkaData) {
              setKepkaInfo(kepkaData);
              console.log('✅ Kepka info loaded:', kepkaData);
            }
          } catch (error) {
            console.error('❌ Error fetching kepka info:', {
              kepka_id: detailData.kepka_id,
              status: error.response?.status,
              message: error.message
            });
            setKepkaInfo(null);
          }
        }

        setCatatan(detailData.datacatatan || {});
        let initialDocs = [];
        if (detailData.verification_notes && Array.isArray(detailData.verification_notes) && detailData.verification_notes.length > 0) {
          initialDocs = detailData.verification_notes.sort((a, b) => a.index - b.index);
        }
        setVerificationDocs(initialDocs);

      } catch (err) {
        if (err.response && err.response.status === 404) {
          setError('Data dengan tiket ini sudah tidak ada atau telah dihapus.');
        } else {
          setError('Gagal memuat detail data. Silakan coba lagi.');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    const handleScroll = () => setIsVisible(window.pageYOffset > 300);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [no_tiket, navigate]);

  if (loading) return <div className="loading-container"><div className="loading-spinner"></div><p>Memuat data...</p></div>;
  if (error) return <div className="error-container"><div className="alert alert-danger">{error}</div></div>;

  const isFormActionable = dataDetail && dataDetail.status === 'Sedang Verifikasi';

  return (
    <DashboardLayout menuItems={getSupervisorMenuItems()} userRole="Supervisor">
      <ConfirmationModal 
        isOpen={isConfirmModalOpen} 
        onClose={() => {
          setIsConfirmModalOpen(false);
          setSiapPublikasi(null); // Reset saat tutup
        }} 
        onConfirm={handleConfirmSubmit} 
        loading={isSaving} 
        ticketStatus={statusChoices.tiket}
        siapPublikasi={siapPublikasi}
        onSiapPublikasiChange={setSiapPublikasi}
        jenisIgt={dataDetail?.jenis_igt}
      />
      <UnsealProgressModal
        isOpen={isUnsealModalOpen}
        onClose={closeUnsealModal}
        onComplete={handleUnsealComplete}
        noTiket={selectedTicket?.noTiket}
        namaIgt={selectedTicket?.namaIgt}
        mode="unseal"
      />
      <ToastContainer position="top-center" autoClose={3000} theme="colored" />
      <div className="container mt-4">
        {/* Breadcrumb */}
        <nav aria-label="breadcrumb" className="mb-3">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="/admin">Dashboard</a>
            </li>
            <li className="breadcrumb-item">
              <a href="/admin/data">Daftar Data</a>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              Persetujuan Data - {dataDetail?.no_tiket}
            </li>
          </ol>
        </nav>

        {dataDetail && (
          <div className="rekap-detail-wrapper">
            {/* Header Card */}
            <div className="rekap-header-card">
              <button onClick={() => navigate(-1)} className="close-button"><X size={20} /></button>
              <div className="header-content">
                <h2 className="rekap-title">Persetujuan Akhir Verifikasi</h2>
                <div className="ticket-info">
                  <span className="ticket-label">No. Tiket:</span>
                  <span className="ticket-number">{dataDetail.no_tiket}</span>
                </div>
              </div>
            </div>

            {/* Alert jika verifikasi belum selesai */}
            {!isFormActionable && dataDetail.status !== 'Sudah Terintegrasi' && dataDetail.status !== 'Perlu Perbaikan' && (
              <div className="alert alert-warning mt-3">
                <AlertTriangle size={16} className="me-2" />
                <strong>Perhatian:</strong> Verifikator belum mengirimkan hasil verifikasi untuk tiket ini.
                Form persetujuan akhir belum dapat diproses.
              </div>
            )}

            {/* Info Pengirim & Verifikator */}
            <div className="files-section">
              <h3 className="section-title"><User className="me-2" />Informasi Pengirim</h3>
              <div className="status-summary-card">
                <div className="summary-grid">
                  <div className="summary-item"><label>Nama IGT</label><span>{dataDetail.nama_igt}</span></div>
                  <div className="summary-item"><label>Jenis IGT</label><span>{dataDetail.jenis_igt}</span></div>
                  <div className="summary-item"><label>Pengirim</label><span>{profil?.fullname || '-'}</span></div>
                  <div className="summary-item"><label>K/L</label><span>{dataDetail.kl}</span></div>
                  <div className="summary-item"><label>Verifikator</label><span>{dataDetail.fullname || '-'}</span></div>
                  <div className="summary-item"><label>Status Saat Ini</label><span className={`status-badge status-${(dataDetail.status || "").toLowerCase().replace(/ /g, '-')}`}>{dataDetail.status}</span></div>
                  {dataDetail.jenis_igt === 'Renaksi PKSP' && (
                    <div className="summary-item">
                      <label>Status Publikasi</label>
                      {dataDetail.status_publikasi ? (
                        <span className={`status-badge status-${dataDetail.status_publikasi.toLowerCase().replace(/ /g, '-')}`}>
                          {dataDetail.status_publikasi}
                        </span>
                      ) : (
                        <span className="status-badge" style={{ backgroundColor: '#6c757d', color: '#fff' }}>-</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Dokumen Catatan Verifikator */}
            <div className="files-section">
              <h3 className="section-title"><FileText className="me-2" />Dokumen Catatan dari Verifikator: {dataDetail.fullname || ''}</h3>
              <div className="status-summary-card">
                <div className="p-3">
                  {verificationDocs.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {verificationDocs.map((doc, i) => (
                        <button
                          key={doc.index}
                          onClick={() => downloadVerificationDoc(doc.index)}
                          className="file-download-card"
                        >
                          <FileCheck className="file-icon-start" size={20} />
                          <span className="file-label">
                            {`Unduh Dokumen Catatan ${i + 1}`}
                          </span>
                          <DownloadCloud className="file-icon-end" size={18} />
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="no-file-indicator mb-0">Dokumen catatan verifikator tidak tersedia.</p>
                  )}
                </div>
              </div>
            </div>

            {/* Detail Hasil Verifikasi */}
            <div className="detail-table-section">
              <h3 className="section-title"><FileCheck className="me-2" />Hasil Verifikasi Dokumen</h3>
              <div className="table-wrapper">
                <div className="table-container">
                  <table className="detail-table">
                    <thead><tr><th className="col-document">Dokumen</th><th className="col-file">File Pengajuan</th><th className="col-notes">Catatan Verifikator</th><th className="col-status">Status</th></tr></thead>
                    <tbody>
                      {documentItems.map((item) => {
                        const noteParts = (catatan && catatan[item.key]) ? String(catatan[item.key]).split('|') : [];
                        const docNote = noteParts[0] || '-';
                        const docStatus = noteParts.length > 1 ? noteParts[1] : '-';

                        return (
                          <tr key={item.key}>
                            <td className="document-cell"><div className="document-info">{item.icon}<span className="dok-name">{item.label}</span></div></td>
                            <td className="file-cell">
                              {dataDetail[item.key] ? (
                                <div className="file-download-container">
                                  {item.key === 'peta' && dataDetail.status_enkripsi === 'sealed' ? (
                                    <button
                                      onClick={() =>
                                        openUnsealModal({
                                          noTiket: dataDetail.no_tiket,
                                          namaIgt: dataDetail.nama_igt,
                                        })
                                      }
                                      className="file-download-btn"
                                    >
                                      <Download size={14} />
                                      <span>Unduh File {item.label}</span>
                                    </button>
                                  ) : item.key === 'metadata' ? (
                                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                      <a
                                        href={`${config.API_URL}/download/${btoa(dataDetail[item.key].split('/').pop())}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="file-download-btn"
                                        title={`Download ${item.label}`}
                                      >
                                        <FileText size={16} />
                                        <span>Unduh File</span>
                                        <Download size={14} />
                                      </a>
                                      {dataDetail[item.key].toLowerCase().endsWith('.xml') && (
                                        <button
                                          onClick={handleOpenMetadataEditor}
                                          className="file-download-btn"
                                          style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' }}
                                        >
                                          <Edit3 size={14} />
                                          <span>Lihat Metadata</span>
                                        </button>
                                      )}
                                    </div>
                                  ) : (
                                    <a
                                      href={`${config.API_URL}/download/${btoa(dataDetail[item.key].split('/').pop())}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="file-download-btn"
                                      title={`Download ${item.label}`}
                                    >
                                      <FileText size={16} />
                                      <span>Unduh File {item.label}</span>
                                      <Download size={14} />
                                    </a>
                                  )}
                                </div>
                              ) : (
                                <div className="no-file-indicator">
                                  <span>File tidak tersedia</span>
                                </div>
                              )}
                            </td>
                            <td className="notes-cell"><p className="notes-content">{docNote}</p></td>
                            <td className="status-cell">
                              <div className="status-with-checklist">
                                <div className={`status-badge ${docStatus === 'Sesuai' ? 'status-approved' : 'status-rejected'}`}>{docStatus}</div>
                                {availableChecklists[item.key] && (
                                  <button
                                    onClick={() => handleChecklistViewOpen(item.key)}
                                    className={`checklist-view-btn-icon ${checklistStatus[item.key] ? 'has-data' : 'no-data'}`}
                                    title={checklistStatus[item.key] ? "Lihat Hasil Checklist" : "Checklist Belum Diisi"}
                                  >
                                    <ClipboardCheck size={16} />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                      <tr>
                        <td className="document-cell"><div className="document-info"><Globe className="document-lucide-icon" /><span className="dok-name">Cakupan</span></div></td>
                        <td className="file-cell"><div className="cakupan-text-inline">{dataDetail.cakupan || "-"}</div></td>
                        <td className="notes-cell"><p className="notes-content">{(catatan?.cakupan?.split('|')[0] || '-')}</p></td>
                        <td className="status-cell">
                          <div className="status-with-checklist">
                            <div className={`status-badge ${catatan?.cakupan?.split('|')[1] === 'Sesuai' ? 'status-approved' : 'status-rejected'}`}>{catatan?.cakupan?.split('|')[1] || '-'}</div>
                            {availableChecklists['cakupan'] && (
                              <button
                                onClick={() => handleChecklistViewOpen('cakupan')}
                                className={`checklist-view-btn-icon ${checklistStatus['cakupan'] ? 'has-data' : 'no-data'}`}
                                title={checklistStatus['cakupan'] ? "Lihat Hasil Checklist" : "Checklist Belum Diisi"}
                              >
                                <ClipboardCheck size={16} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Data Capaian Section (Read-Only) */}
            {capaianData && capaianData.length > 0 && (
              <div className="files-section" style={{ marginTop: '30px' }}>
                <h3 className="section-title">
                  <FileCheck className="me-2" size={22} />
                  Data Capaian
                </h3>
                
                {/* Kepka Info */}
                {kepkaInfo && (
                  <div style={{
                    padding: '12px 16px',
                    backgroundColor: '#eff6ff',
                    border: '1px solid #bfdbfe',
                    borderRadius: '8px',
                    marginBottom: '16px'
                  }}>
                    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '14px' }}>
                      <span><strong>Nama IGT Walidata:</strong> {kepkaInfo.nama_igt_walidata}</span>
                      {kepkaInfo.nama_igt_renaksi && (
                        <span><strong>Nama IGT Renaksi:</strong> {kepkaInfo.nama_igt_renaksi}</span>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Capaian Table (Read-Only) */}
                <div className="table-wrapper">
                  <div className="table-container">
                    <table className="detail-table">
                      <thead>
                        <tr>
                          <th style={{ width: '100px', textAlign: 'center' }}>Tahun</th>
                          <th style={{ width: '150px', textAlign: 'center' }}>Capaian</th>
                          <th style={{ width: '150px', textAlign: 'center' }}>Target</th>
                          <th style={{ width: '120px', textAlign: 'center' }}>Satuan</th>
                          <th style={{ width: '120px', textAlign: 'center' }}>Persentase</th>
                        </tr>
                      </thead>
                      <tbody>
                        {capaianData
                          .filter(item => item.nilai && item.nilai !== '-' && item.nilai !== '')
                          .map((item, index) => (
                            <tr key={index}>
                              <td style={{ textAlign: 'center', fontWeight: '600' }}>{item.tahun}</td>
                              <td style={{ textAlign: 'center' }}>{item.nilai}</td>
                              <td style={{ textAlign: 'center' }}>{item.target || '-'}</td>
                              <td style={{ textAlign: 'center' }}>{item.satuan || '-'}</td>
                              <td style={{ textAlign: 'center' }}>
                                <span 
                                  className="badge" 
                                  style={{
                                    backgroundColor: parseFloat(item.persentase) >= 50 ? '#10b981' : '#f59e0b',
                                    color: 'white',
                                    padding: '6px 12px',
                                    fontSize: '13px',
                                    fontWeight: '600'
                                  }}
                                >
                                  {item.persentase || 0}%
                                </span>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                
                {/* Target Info from Kepka */}
                {kepkaInfo && kepkaInfo.target && (
                  <div className="target-capaian-modern">
                    <div className="target-header">
                      <div className="target-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"/>
                          <circle cx="12" cy="12" r="6"/>
                          <circle cx="12" cy="12" r="2"/>
                        </svg>
                      </div>
                      <h4>Target Capaian</h4>
                    </div>
                    <div className="target-grid">
                      {Object.entries(kepkaInfo.target).map(([year, target]) => (
                        <div key={year} className="target-card">
                          <div className="target-year">{year}</div>
                          <div className="target-value">
                            <span className="value-number">{target}</span>
                            <span className="value-unit">{kepkaInfo.satuan}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Dokumen Hasil Verifikasi yang Sudah Ada */}
            {(dataDetail.batte || dataDetail.rekom) && (
              <div className="files-section">
                <h3 className="section-title"><Download className="me-2" />Dokumen Hasil Verifikasi</h3>
                <div className="files-grid">
                  {/* Rekomendasi Integrasi (BA) */}
                  {dataDetail.batte && (
                    <div className="file-card">
                      <div className="file-icon">
                        <Download size={24} />
                      </div>
                      <div className="file-info">
                        <h4>Rekomendasi Integrasi dan Berbagi Pakai</h4>
                        <a
                          href={`${config.API_URL}/downloadsurat/${btoa(dataDetail.batte.split('/').pop())}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="file-download-link"
                        >
                          Unduh Dokumen
                        </a>
                      </div>
                    </div>
                  )}

                  {/* Rekomendasi Hasil Verifikasi (Rekom) */}
                  {dataDetail.rekom && (
                    <div className="file-card">
                      <div className="file-icon">
                        <Download size={24} />
                      </div>
                      <div className="file-info">
                        <h4>Rekomendasi Hasil Verifikasi</h4>
                        <p className="verifier-name">Supervisor: {dataDetail.supervisor_name || 'N/A'}</p>
                        <a
                          href={`${config.API_URL}/downloadsurat/${btoa(dataDetail.rekom.split('/').pop())}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="file-download-link"
                        >
                          Unduh Dokumen
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Opsi Keputusan Akhir */}
            <form onSubmit={handleSubmitForm}>
              <div className="detail-table-section">
                <h3 className="section-title"><Send className="me-2" />Keputusan Akhir dan Unggah Dokumen</h3>
                <div className="p-3">
                  <div className="mb-4">
                    <label className="form-label fw-bold">Close Tiket?</label>
                    <select
                      className={`form-select form-select-lg ${statusError ? 'is-invalid' : ''}`}
                      value={statusChoices.tiket || ''}
                      onChange={(e) => handleStatusChange(e, 'tiket')}
                      disabled={!isFormActionable}
                    >
                      <option value="">-- Pilih Status Akhir --</option>
                      <option value="Tidak">❌ Tolak dan Kirim Rekomendasi Perbaikan</option>
                      <option value="OK">✅ Setujui dan Kirim Rekomendasi Integrasi</option>
                    </select>
                    {statusError && <div className="invalid-feedback">{statusError}</div>}
                  </div>

                  {statusChoices.tiket && isFormActionable && (
                    <div className={`upload-section ${statusChoices.tiket === 'OK' ? 'upload-section-success' : 'upload-section-danger'}`}>
                      {statusChoices.tiket === 'OK' && (
                        <div>
                          <h5 className="upload-title">Rekomendasi Integrasi dan Berbagi Pakai</h5>
                          <button
                            type="button"
                            onClick={() => downloadTemplate('ba')}
                            className="btn btn-sm btn-outline-dark mb-2"
                            disabled={downloadingTemplate.ba}
                          >
                            {downloadingTemplate.ba ? (
                              <>
                                <div className="spinner-border spinner-border-sm me-2" role="status">
                                  <span className="visually-hidden">Loading...</span>
                                </div>
                                Mengunduh...
                              </>
                            ) : (
                              <>
                                <DownloadCloud size={14} /> Unduh dan Generate Dokumen
                              </>
                            )}
                          </button>

                          {hasDownloaded.ba ? (
                            <input type="file" ref={baRef} className="form-control" accept=".pdf" />
                          ) : (
                            <div className="alert alert-info mt-2">
                              <Info size={14} /> Klik tombol unduh untuk mendapatkan dokumen "Rekomendasi Integrasi dan Berbagi Pakai" terlebih dahulu
                            </div>
                          )}

                          {dataDetail.batte && (
                            <div className="file-existing mt-3" style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '12px 16px',
                              backgroundColor: '#d1fae5',
                              border: '1px solid #10b981',
                              borderRadius: '8px',
                              color: '#065f46',
                              fontSize: '14px',
                              fontWeight: '500'
                            }}>
                              <FileCheck size={16} style={{ flexShrink: 0 }} />
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: '600', marginBottom: '4px' }}>File Sudah Diupload</div>
                                <div style={{ fontSize: '13px', color: '#047857', wordBreak: 'break-all' }}>
                                  {decodeURIComponent(dataDetail.batte.split('/').pop().replace(/^[a-f0-9-]+_/, ''))}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {statusChoices.tiket === 'Tidak' && (
                        <div>
                          <h5 className="upload-title">Rekomendasi Hasil Verifikasi</h5>
                          <button
                            type="button"
                            onClick={() => downloadTemplate('rekom')}
                            className="btn btn-sm btn-outline-dark mb-2"
                            disabled={downloadingTemplate.rekom}
                          >
                            {downloadingTemplate.rekom ? (
                              <>
                                <div className="spinner-border spinner-border-sm me-2" role="status">
                                  <span className="visually-hidden">Loading...</span>
                                </div>
                                Mengunduh...
                              </>
                            ) : (
                              <>
                                <DownloadCloud size={14} /> Unduh dan Generate Dokumen
                              </>
                            )}
                          </button>

                          {hasDownloaded.rekom ? (
                            <input type="file" ref={suratRekomendasiRef} className="form-control" accept=".pdf" />
                          ) : (
                            <div className="alert alert-info mt-2">
                              <Info size={14} /> Klik tombol unduh untuk mendapatkan dokumen "Rekomendasi Hasil Verifikasi" terlebih dahulu
                            </div>
                          )}

                          {dataDetail.rekom && (
                            <div className="file-existing mt-3" style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '12px 16px',
                              backgroundColor: '#d1fae5',
                              border: '1px solid #10b981',
                              borderRadius: '8px',
                              color: '#065f46',
                              fontSize: '14px',
                              fontWeight: '500'
                            }}>
                              <FileCheck size={16} style={{ flexShrink: 0 }} />
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: '600', marginBottom: '4px' }}>File Sudah Diupload</div>
                                <div style={{ fontSize: '13px', color: '#047857', wordBreak: 'break-all' }}>
                                  {decodeURIComponent(dataDetail.rekom.split('/').pop().replace(/^[a-f0-9-]+_/, ''))}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="mt-4 text-center">
                  <button
                    type="submit"
                    className="btn-submit"
                    disabled={isSaving || !isFormActionable}
                  >
                    <Send size={16} />
                    {isSaving ? 'Memproses...' : 'Kirim'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}
      </div>
      {isVisible && <button onClick={scrollToTop} className="scroll-to-top"><ArrowUp size={24} /></button>}

      <DataChecklistView
        isOpen={isChecklistViewOpen}
        onClose={handleChecklistViewClose}
        documentType={selectedDocumentType}
        noTiket={dataDetail?.no_tiket}
      />

      {/* Metadata Editor Modal */}
      {showMetadataEditor && (
        <MetadataEditor
          isOpen={showMetadataEditor}
          onClose={handleCloseMetadataEditor}
          metadataFile={metadataContent}
          readOnly={true}
        />
      )}

      <UnsealProgressModal
        isOpen={isUnsealModalOpen}
        onClose={closeUnsealModal}
        ticketData={selectedTicket}
      />
    </DashboardLayout>
  );
};

export default DataSVDetailPage;